import { useState, useMemo } from "react";
import { readSettings } from "@/hooks/use-settings";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmt, ResultCard } from "./pipe-helpers";
import { drainSizingRows, ventSizingRows, waterSupplyRows, HUNTERS_CURVE } from "./plumbing-reference-data";

/* ═══════════════════════════════════════════════════════
   DRAIN PIPE SIZING CALCULATOR
   ═══════════════════════════════════════════════════════ */
export const DrainSizingCalculator = () => {
  const defaults = readSettings();
  const [dfu, setDfu] = useState("");
  const [slope, setSlope] = useState<string>(defaults.drainSlope);

  const slopeMap: Record<string, number> = { "1/16": 1, "1/8": 2, "1/4": 3, "1/2": 4 };
  const colIdx = slopeMap[slope] ?? 3;
  const dfuVal = parseFloat(dfu);

  const result = useMemo(() => {
    if (!(dfuVal > 0)) return null;
    for (const row of drainSizingRows) {
      const capacity = row[colIdx];
      if (capacity === "—") continue;
      if (typeof capacity === "number" && capacity >= dfuVal) {
        return { size: row[0] as string, capacity };
      }
    }
    return null;
  }, [dfuVal, colIdx]);

  return (
    <div className="px-4 space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Enter your total <span className="text-foreground font-semibold">DFU load</span> and pipe slope to find the minimum drain pipe size per code.
        </p>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">
            Total DFU Load
          </label>
          <Input type="number" inputMode="decimal" placeholder="Enter total DFUs…" value={dfu} onChange={(e) => setDfu(e.target.value)} className="font-mono-data bg-card border-border" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">
            Pipe Slope (in/ft)
          </label>
          <Select value={slope} onValueChange={setSlope}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1/16">1/16" per foot</SelectItem>
              <SelectItem value="1/8">1/8" per foot</SelectItem>
              <SelectItem value="1/4">1/4" per foot</SelectItem>
              <SelectItem value="1/2">1/2" per foot</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {result && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Minimum Drain Size" value={result.size} unit="" accent />
          <ResultCard label="Max DFU Capacity" value={String(result.capacity)} unit="DFU" />
          <ResultCard label="Your DFU Load" value={fmt(dfuVal)} unit="DFU" />
          <ResultCard label="Slope" value={`${slope}"`} unit="per ft" />
        </div>
      )}
      {dfuVal > 0 && !result && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-3 animate-fade-in">
          <p className="text-xs text-destructive font-semibold">⚠️ DFU load exceeds table capacity at this slope. Consider a larger pipe or steeper slope.</p>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   VENT PIPE SIZING CALCULATOR
   ═══════════════════════════════════════════════════════ */
export const VentSizingCalculator = () => {
  const [dfu, setDfu] = useState("");
  const [devLength, setDevLength] = useState("50");

  const lengthMap: Record<string, number> = { "30": 1, "50": 2, "100": 3, "150": 4, "200": 5 };
  const colIdx = lengthMap[devLength] ?? 2;
  const dfuVal = parseFloat(dfu);

  const result = useMemo(() => {
    if (!(dfuVal > 0)) return null;
    for (const row of ventSizingRows) {
      const capacity = row[colIdx];
      if (capacity === "—") continue;
      if (typeof capacity === "number" && capacity >= dfuVal) {
        return { size: row[0] as string, capacity };
      }
    }
    return null;
  }, [dfuVal, colIdx]);

  return (
    <div className="px-4 space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Size vent pipes based on the <span className="text-foreground font-semibold">DFU load</span> and the <span className="text-foreground font-semibold">developed length</span> of the vent.
        </p>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">
            Total DFU Load
          </label>
          <Input type="number" inputMode="decimal" placeholder="Enter total DFUs…" value={dfu} onChange={(e) => setDfu(e.target.value)} className="font-mono-data bg-card border-border" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">
            Developed Length (ft)
          </label>
          <Select value={devLength} onValueChange={setDevLength}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="30">30 ft</SelectItem>
              <SelectItem value="50">50 ft</SelectItem>
              <SelectItem value="100">100 ft</SelectItem>
              <SelectItem value="150">150 ft</SelectItem>
              <SelectItem value="200">200 ft</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {result && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Minimum Vent Size" value={result.size} unit="" accent />
          <ResultCard label="Max DFU Capacity" value={String(result.capacity)} unit="DFU" />
          <ResultCard label="Your DFU Load" value={fmt(dfuVal)} unit="DFU" />
          <ResultCard label="Developed Length" value={devLength} unit="ft" />
        </div>
      )}
      {dfuVal > 0 && !result && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-3 animate-fade-in">
          <p className="text-xs text-destructive font-semibold">⚠️ DFU load exceeds vent table capacity at this length. Try a shorter run or larger pipe.</p>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   WATER SUPPLY PIPE SIZING CALCULATOR
   ═══════════════════════════════════════════════════════ */
export const WaterSupplySizingCalculator = () => {
  const [wsfu, setWsfu] = useState("");

  const wsfuVal = parseFloat(wsfu);

  const result = useMemo(() => {
    if (!(wsfuVal > 0)) return null;
    for (const row of waterSupplyRows) {
      if (typeof row[1] === "number" && row[1] >= wsfuVal) {
        return { size: row[0] as string, maxWsfu: row[1] as number, gpm: row[2] as number };
      }
    }
    return null;
  }, [wsfuVal]);

  // Interpolate Hunter's Curve
  const estimatedGpm = useMemo(() => {
    if (!(wsfuVal > 0)) return null;
    const curve = HUNTERS_CURVE;
    if (wsfuVal <= curve[0].wsfu) return curve[0].gpm;
    if (wsfuVal >= curve[curve.length - 1].wsfu) return curve[curve.length - 1].gpm;
    for (let i = 0; i < curve.length - 1; i++) {
      if (wsfuVal >= curve[i].wsfu && wsfuVal <= curve[i + 1].wsfu) {
        const ratio = (wsfuVal - curve[i].wsfu) / (curve[i + 1].wsfu - curve[i].wsfu);
        return curve[i].gpm + ratio * (curve[i + 1].gpm - curve[i].gpm);
      }
    }
    return null;
  }, [wsfuVal]);

  return (
    <div className="px-4 space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Enter total <span className="text-foreground font-semibold">Water Supply Fixture Units</span> (WSFU) to find the minimum supply pipe size and estimated demand using Hunter's Curve.
        </p>
      </div>
      <div>
        <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">
          Total WSFU
        </label>
        <Input type="number" inputMode="decimal" placeholder="Enter total WSFU…" value={wsfu} onChange={(e) => setWsfu(e.target.value)} className="font-mono-data bg-card border-border" />
      </div>
      {result && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Minimum Supply Pipe" value={result.size} unit="" accent />
          <ResultCard label="Max WSFU Capacity" value={String(result.maxWsfu)} unit="WSFU" />
          {estimatedGpm !== null && (
            <ResultCard label="Est. Demand (Hunter's)" value={fmt(estimatedGpm, 1)} unit="GPM" accent />
          )}
          <ResultCard label="Your WSFU Load" value={fmt(wsfuVal)} unit="WSFU" />
        </div>
      )}
      {wsfuVal > 0 && !result && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-3 animate-fade-in">
          <p className="text-xs text-destructive font-semibold">⚠️ WSFU load exceeds table capacity. Consider a larger main or parallel supplies.</p>
        </div>
      )}
      {estimatedGpm !== null && !result && wsfuVal > 0 && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Est. Demand (Hunter's)" value={fmt(estimatedGpm, 1)} unit="GPM" />
        </div>
      )}
    </div>
  );
};
